import styled from '@emotion/styled'
import dayjs from 'dayjs'
import * as paths from '../../../constants/path'
import React, { useCallback, useMemo, useState } from 'react'
import { useRouter } from '../../../../utils/helper'
import { Authorize } from '../../../components/Authorize'
import Button from '../../../components/common/Button'
import Text from '../../../components/common/Text'
import ListPage from '../../../components/templates/ListPage'

import { ROLE } from '../../../services/user/user-typed'
import { Box } from '@material-ui/core'
import { numberWithCommas } from '../../../../utils/core'
import { appConfig } from '../../../constants/app-config'
import {
  useGetImportDcaFiles,
  useGetImportFileTemplate,
  useUploadImportDcaFile,
} from '../../../services/import-dca/import-dca-query'
import { useToastForReactQuery } from '../../../../utils/custom-hooks'
import { useDropzone } from 'react-dropzone'
import { useOtpMobileByToken } from '../../../services/otp/otp-query'
import { OtpAction } from '../../../services/otp/otp-types'
import { useTranslation } from 'react-i18next'
import OTPModal from '../../../components/common/OTPModal'
import {
  EnumImportDcaFileStatus,
  EnumImportDcaRecordStatus,
  IImportDcaFile,
} from '../../../services/import-dca/import-dca-typed'
import { useHistory } from 'react-router-dom'
import ModalExportDcaRecord from '../ImportDcaRecordList/ModalExportDcaRecord'

const ButtonStyled = styled(Button)`
  padding: 7px 13px;
  margin-right: 16px;
`

const SummaryLayout = styled.ul`
  padding-inline-start: 10px;

  > li {
    white-space: nowrap;
  }
`

const StatusLayout = styled.div`
  padding: 2px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border-radius: 30px;

  > p {
    margin: 0;
  }
`

const Layout = styled.div`
  display: flex;
  flex: 1;
  flex-flow: row;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  padding-right: 16px;
`
type OTPState = {
  id: string
  refCode: string
}

const ImportDcaList = () => {
  const { push, query } = useRouter()
  const { t } = useTranslation()
  const history = useHistory()

  const toast = useToastForReactQuery()
  const { mutate: getTemplateFile } = useGetImportFileTemplate()
  const { q, page } = query
  const { data: importDcaFiles } = useGetImportDcaFiles({ q, page })
  const [otpState, setOtpState] = useState<OTPState | null>(null)
  const [fileToUpload, setFileToUpload] = useState<File | null>(null)
  const [otpErrorMessage, setOtpErrorMessage] = useState('')
  const { mutate: uploadFile } = useUploadImportDcaFile()
  const [otpModalVisible, setOtpModalVisible] = useState(false)
  const [exportModalVisible, setExportModalVisible] = useState(false)

  const onOpenExportDcaRecordModal = useCallback(() => {
    setExportModalVisible(true)
  }, [])

  const onCloseExportDcaRecordModal = useCallback(() => {
    setExportModalVisible(false)
  }, [])

  const { mutateAsync: requestOtp } = useOtpMobileByToken()

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]

        try {
          const otpResponse = await requestOtp({
            action: OtpAction.IMPORT_DCA_FILE,
          })
          setOtpState({ id: otpResponse.otpId, refCode: otpResponse.refCode })
          setFileToUpload(file)
          setOtpModalVisible(true)
        } catch (error) {
          toast.open(t(`${error}`), {
            type: 'error',
            autoClose: 3 * 1000,
          })
        }
      }
    },
    [requestOtp, t, toast],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
  })

  const onOpenExportModal = useCallback(() => {
    getTemplateFile(undefined, {
      onSuccess: () => {
        toast.open('Success', {
          type: 'success',
          autoClose: 3 * 1000,
        })
      },
      onError: error => {
        toast.open(error, {
          type: 'error',
          autoClose: 3 * 1000,
        })
      },
    })
  }, [getTemplateFile, toast])

  const onClickViewDetail = useCallback(
    (id: string) => {
      history.push(paths.dcaRecordList({ routeParam: { id } }))
    },
    [history],
  )

  const columns = useMemo(() => {
    return [
      {
        title: 'File Name',
        dataIndex: 'fileName',
      },
      {
        title: 'Create At',
        dataIndex: 'createdAt',
        render: (text: string) => {
          return <Text>{dayjs(text).format('D MMMM  YYYY HH:mm ')}</Text>
        },
      },
      {
        title: 'Updated At',
        dataIndex: 'updatedAt',
        render: (text: string) => {
          return <Text>{dayjs(text).format('D MMMM  YYYY HH:mm ')}</Text>
        },
      },
      {
        title: 'Progress',
        dataIndex: 'summary',
        render: (_: unknown, record: IImportDcaFile) => {
          const { summary } = record
          return (
            <SummaryLayout
              style={{
                paddingInlineStart: '10px',
              }}
            >
              <li>Total: {numberWithCommas(summary.total, 0)}</li>
              <li>Pending: {numberWithCommas(summary.countPending, 0)}</li>
              <li>Processing: {numberWithCommas(summary.countProcessing, 0)}</li>
              <li>Success: {numberWithCommas(summary.countSuccess, 0)}</li>
              <li>Fail: {numberWithCommas(summary.countFail, 0)}</li>
            </SummaryLayout>
          )
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        render: (status: string) => {
          const color = (status: EnumImportDcaRecordStatus) => {
            switch (status) {
              case EnumImportDcaRecordStatus.FAIL:
                return '#ff0000'
              case EnumImportDcaRecordStatus.SUCCESS:
                return '#00b050'
              case EnumImportDcaRecordStatus.PENDING:
                return '#ffc000'
              default:
                return '#1E90FF'
            }
          }
          return (
            <StatusLayout color={color(status as EnumImportDcaRecordStatus)}>
              <Text bold>{status}</Text>
            </StatusLayout>
          )
        },
      },
      {
        title: 'Error',
        dataIndex: 'errorMessage',
        render: (errorMessage: string) => {
          return (
            <Text
              bold
              style={{
                maxWidth: '200px',
                wordBreak: 'break-all',
              }}
            >
              {errorMessage}
            </Text>
          )
        },
      },
      {
        title: '',
        dataIndex: 'action',
        render: (text: string, importDcaFile: IImportDcaFile) => {
          return importDcaFile.status === EnumImportDcaFileStatus.SUCCESS ? (
            <Button onClick={onClickViewDetail.bind(null, importDcaFile.id)}>View</Button>
          ) : (
            <></>
          )
        },
      },
    ]
  }, [onClickViewDetail])

  const setQueryParam = useCallback(
    params => {
      push(paths.bulkDca({ queryParam: { ...query, ...params } }))
    },
    [push, query],
  )
  const onSearch = useCallback(
    text => {
      setQueryParam({ q: text, page: 1 })
    },
    [setQueryParam],
  )

  const onPageChange = useCallback(
    page => {
      setQueryParam({ page })
    },
    [setQueryParam],
  )

  const handleOtpConfirm = useCallback(
    (otp: string) => {
      const body = { file: fileToUpload, otpData: { otp, otpId: otpState?.id } }

      if (otpState && fileToUpload) {
        uploadFile(body, {
          onError: error => {
            if (error === '422-022') {
              setOtpErrorMessage(t(error))
            } else {
              toast.open('upload file fail', {
                type: 'error',
                autoClose: 3 * 1000,
              })
              setOtpModalVisible(false)
            }
          },
          onSuccess: () => {
            toast.open('Success', {
              type: 'success',
              autoClose: 3 * 1000,
            })
            setOtpErrorMessage('')
            setOtpModalVisible(false)
          },
        })
      }
    },
    [fileToUpload, otpState, uploadFile, t, toast],
  )
  //
  return appConfig.ENABLE_FEATURE_IMPORT_DCA ? (
    <Authorize role={ROLE.FINANCE}>
      <ListPage
        topbar={
          <Layout>
            <Authorize role={ROLE.FINANCE}>
              <ButtonStyled variant="primary" onClick={onOpenExportModal}>
                Example File
              </ButtonStyled>

              <ButtonStyled {...getRootProps({ className: 'dropzone' })} variant="outline-primary">
                Import File
                <input {...getInputProps()} />
              </ButtonStyled>
              <ButtonStyled variant="outline-warning" onClick={onOpenExportDcaRecordModal}>
                Export
              </ButtonStyled>
            </Authorize>
          </Layout>
        }
        tableProps={{
          columns,
          data: importDcaFiles?.items || [],
          onPageChange,
          pagination: importDcaFiles?.meta,
        }}
        searchInputProps={{ onSearch, value: query.q, placeholder: 'Search title' }}
      />
      <OTPModal
        show={otpModalVisible}
        handleClose={() => {
          setOtpErrorMessage('')
          setOtpModalVisible(false)
        }}
        handleConfirm={handleOtpConfirm}
        otpRef={otpState?.refCode}
        errorMessage={otpErrorMessage}
      />
      <ModalExportDcaRecord visible={exportModalVisible} onClose={onCloseExportDcaRecordModal} />
    </Authorize>
  ) : (
    <></>
  )
}

export default ImportDcaList
